/**
 * DesignView — Live-Vorschau der Wireframe-Pages mit Style-Guide-Override.
 *
 * Fuehrt drei Datenquellen zusammen:
 *   - Sitemap (sitemapPages): Page-Hierarchie + Pflichtseiten + Page-Type
 *   - Wireframe (wireframeData): Block-Slugs + Slot-Werte pro Page
 *   - Style-Guide (styleGuide): alle Tokens (Farben/Typo/Spacing/UI/Forms/...)
 *
 * Rendert pro aktiver Page das HTML aus der Library-Templates mit Slot-
 * Werten. CSS-Override-Block mappt die neutralen Tailwind-Gray-Klassen
 * (Phase B) auf die Style-Guide-Tokens — die Library bleibt Wireframe-only,
 * das Brand-Design entsteht erst hier zur Render-Zeit.
 *
 * Props:
 *   projectId
 *   sitemapPages          — [{id, page_name, parent_id, page_type, ist_pflichtseite, ...}]
 *   wireframeData         — { pages: [{page_id, page_name, blocks: [...]}] }
 *   styleGuide            — Style-Tokens aus StyleGuideView
 *   approved              — Style-Guide muss freigegeben sein, sonst Lock
 *   onOpenGrapesJS        — Callback (Container öffnet Editor-Modal/Tab)
 *   onNetlifyDeploy       — Callback (Container ruft Deploy-Endpoint)
 *   onPageUpdated         — Callback mit der gespeicherten Seite nach der Übernahme
 *
 * „Auf die Seite übernehmen" schreibt die Vorschau nach
 * `sitemap_pages.mockup_html`. Das ist die Stelle, an der dieser Zweig
 * (Sitemap → Wireframe → Style-Guide) den Weg zur ausgelieferten Seite findet:
 * von `mockup_html` geht es in GrapesJS und von dort in den Netlify-Deploy.
 * Ohne diesen Schritt endete alles hier in einem Bild auf dem Schirm.
 */
import { useEffect, useMemo, useState } from 'react';
import API_BASE_URL from '../../config';
import { useAuth } from '../../context/AuthContext';
import { buildOverrideCSS } from '../../utils/brandOverride';
import { blockMarkup, seitenHtml } from '../../utils/pageHtml';

const KC_DARK = 'var(--kc-dark)';
const KC_MID = 'var(--kc-mid)';
const KC_YELLOW = 'var(--kc-yellow)';

export default function DesignView({
  projectId,
  sitemapPages = [],
  wireframeData,
  styleGuide,
  approved,
  onOpenGrapesJS,
  onNetlifyDeploy,
  onPageUpdated,
}) {
  const { token } = useAuth();
  const headers = useMemo(
    () => ({ Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }),
    [token],
  );

  const wfPages = wireframeData?.pages || [];
  const [activePageId, setActivePageId] = useState(wfPages[0]?.page_id || null);
  const [library, setLibrary] = useState({});
  const [libraryLoading, setLibraryLoading] = useState(false);

  useEffect(() => {
    if (!activePageId && wfPages.length > 0) {
      setActivePageId(wfPages[0].page_id);
    }
  }, [wfPages, activePageId]);

  // Bibliothek laden — nur slugs aus dem aktiven Wireframe
  useEffect(() => {
    const slugs = new Set();
    wfPages.forEach((p) => (p.blocks || []).forEach((b) => slugs.add(b.slug)));
    const missing = [...slugs].filter((s) => !library[s]);
    if (missing.length === 0) return;

    setLibraryLoading(true);
    Promise.all(
      missing.map((slug) =>
        fetch(`${API_BASE_URL}/api/components/${slug}`, { headers })
          .then((r) => (r.ok ? r.json() : null))
          .then((data) => [slug, data])
          .catch(() => [slug, null]),
      ),
    )
      .then((results) => {
        setLibrary((prev) => {
          const next = { ...prev };
          results.forEach(([slug, data]) => {
            if (data) next[slug] = data;
          });
          return next;
        });
      })
      .finally(() => setLibraryLoading(false));
  }, [wfPages, headers, library]);

  // ── Sitemap-Tree: Pages nach parent_id gruppieren ────────────────────────
  const sitemapTree = useMemo(() => {
    const byParent = new Map();
    sitemapPages.forEach((p) => {
      const k = p.parent_id ?? 'root';
      if (!byParent.has(k)) byParent.set(k, []);
      byParent.get(k).push(p);
    });
    byParent.forEach((arr) => arr.sort((a, b) => (a.position ?? 0) - (b.position ?? 0)));
    return byParent;
  }, [sitemapPages]);

  // ── Aggregat-Stats für die Status-Bar oben ───────────────────────────────
  const totalBlocks = wfPages.reduce((s, p) => s + (p.blocks?.length || 0), 0);
  const styleHasOverrides =
    Object.keys(styleGuide?.palette_overrides?.light || {}).length +
    Object.keys(styleGuide?.palette_overrides?.dark || {}).length +
    Object.keys(styleGuide?.semantic_overrides?.light || {}).length +
    Object.keys(styleGuide?.semantic_overrides?.dark || {}).length > 0;

  const activePage = wfPages.find((p) => p.page_id === activePageId) || null;
  const activeSitemapPage = sitemapPages.find((p) => p.id === activePageId) || null;

  // ── Style-CSS-Override-Generator ─────────────────────────────────────────
  // Mapped die Tailwind-Gray-Klassen aus der neutralisierten Library
  // (Phase B) auf die aktuellen Style-Guide-Tokens.
  const overrideCSS = useMemo(() => buildOverrideCSS(styleGuide), [styleGuide]);

  // Vorschau und Übernahme teilen sich denselben Baustein — was übernommen
  // wird, muss genau das sein, was hier zu sehen war.
  const renderedHTML = useMemo(
    () => seitenHtml({ blocks: activePage?.blocks, library, overrideCSS }),
    [activePage, library, overrideCSS],
  );

  // ── Übernahme auf die Seite ──────────────────────────────────────────────
  //
  // Bis hierher endete dieser Zweig in einer Vorschau: Sitemap → Wireframe →
  // Style-Guide → Bild auf dem Schirm. Ausgeliefert wurde etwas anderes, das
  // über `mockup_html` in GrapesJS kommt. Dieser Knopf schließt die Lücke —
  // bewusst als Knopf und nicht automatisch, denn er überschreibt, was auf der
  // Seite schon steht.
  const [uebernahme, setUebernahme] = useState({ status: 'idle', text: '' });

  useEffect(() => { setUebernahme({ status: 'idle', text: '' }); }, [activePageId]);

  const uebernehmen = async () => {
    if (!activeSitemapPage || !renderedHTML) return;

    const hatEditorFassung = !!(activeSitemapPage.gjs_html || '').trim();
    const hatEntwurf = !!(activeSitemapPage.mockup_html || '').trim();
    if (hatEditorFassung || hatEntwurf) {
      const was = hatEditorFassung
        ? 'Diese Seite wurde bereits in GrapesJS bearbeitet. Die Übernahme '
          + 'ersetzt den Entwurf; die Editor-Fassung bleibt bestehen, bis du '
          + 'sie dort neu lädst.'
        : 'Diese Seite hat schon einen Entwurf. Die Übernahme ersetzt ihn.';
      // eslint-disable-next-line no-alert
      if (!window.confirm(`${was}\n\nTrotzdem übernehmen?`)) return;
    }

    setUebernahme({ status: 'laeuft', text: 'Übernimmt…' });
    try {
      const res = await fetch(`${API_BASE_URL}/api/sitemap/pages/${activeSitemapPage.id}`, {
        method: 'PUT', headers,
        body: JSON.stringify({ mockup_html: renderedHTML }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        const detail = body?.detail;
        throw new Error(typeof detail === 'string' ? detail : `HTTP ${res.status}`);
      }
      // Nicht der Antwortstatus zählt, sondern was in der Seite steht: Bei
      // Pflichtseiten hat die API früher stillschweigend verworfen.
      if ((body.mockup_html || '') !== renderedHTML) {
        throw new Error('Die Seite hat den Entwurf nicht übernommen.');
      }
      onPageUpdated?.(body);
      setUebernahme({
        status: 'fertig',
        text: 'Übernommen — die Seite geht so in GrapesJS und in den Deploy.',
      });
    } catch (e) {
      setUebernahme({ status: 'fehler', text: e.message || 'Übernahme fehlgeschlagen' });
    }
  };

  const exportHTML = () => {
    const fontFamily = styleGuide?.typography?.font_family || 'Noto Sans';
    const blocksHtml = blockMarkup(activePage?.blocks, library);

    const fullHTML = `<!DOCTYPE html>
<html lang="de">
<head>
<meta charset="UTF-8">
<meta name="viewport" content="width=device-width,initial-scale=1">
<title>${activePage?.page_name || 'Seite'}</title>
<script src="https://cdn.tailwindcss.com"></script>
<style>
body { font-family: '${fontFamily}', sans-serif; margin: 0; padding: 0; }
${overrideCSS}
</style>
</head>
<body>
${blocksHtml}
</body>
</html>`;
    const blob = new Blob([fullHTML], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${activePage?.page_name || 'seite'}.html`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // ── Lock-Screen wenn StyleGuide noch nicht freigegeben ────────────────────
  if (!approved) {
    return (
      <div style={{ padding: 60, textAlign: 'center', fontFamily: 'var(--font-sans, "Noto Sans", sans-serif)' }}>
        <div style={{ fontSize: 48, marginBottom: 12 }}>🔒</div>
        <h1 style={{ fontSize: 20, fontWeight: 900, color: KC_DARK, textTransform: 'uppercase', marginBottom: 8 }}>
          Design gesperrt
        </h1>
        <p style={{ fontSize: 14, color: 'var(--text-secondary)', maxWidth: 480, margin: '0 auto', lineHeight: 1.6 }}>
          Bitte zunächst den Style Guide an den Kunden zur Freigabe senden.
          Sobald die Freigabe vorliegt, wird diese Ansicht automatisch entsperrt.
        </p>
      </div>
    );
  }

  if (wfPages.length === 0) {
    return (
      <div style={{ padding: 24, textAlign: 'center', color: 'var(--text-secondary)', fontFamily: 'var(--font-sans, "Noto Sans", sans-serif)' }}>
        <div style={{ fontSize: 32, marginBottom: 8 }}>🖼</div>
        <div style={{ fontSize: 14, color: KC_DARK, fontWeight: 600 }}>
          Noch kein Wireframe — bitte zuerst die Sitemap- und Wireframe-Schritte abschließen.
        </div>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', fontFamily: 'var(--font-sans, "Noto Sans", sans-serif)' }}>
      {/* Status-Bar oben — zeigt was aus den 3 Quellen einfließt */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap',
        padding: '10px 18px',
        background: 'var(--bg-app)', borderBottom: '1px solid var(--border-light)',
        fontSize: 12,
      }}>
        <SourceBadge label="Sitemap" value={`${sitemapPages.length} Page${sitemapPages.length === 1 ? '' : 's'}`} icon="🗺" />
        <SourceBadge label="Wireframe" value={`${totalBlocks} Block${totalBlocks === 1 ? '' : 's'}`} icon="🧱" />
        <SourceBadge label="Style-Guide" value={styleHasOverrides ? 'Custom' : 'Default'} icon="🎨" />
        <div style={{ flex: 1 }} />
        <span style={{ color: 'var(--text-tertiary)', fontSize: 12 }}>
          {libraryLoading ? '⟳ Templates werden geladen…' : 'Live-Vorschau aktualisiert sich automatisch'}
        </span>
      </div>

      <div style={{ flex: 1, display: 'flex', minHeight: 0, overflow: 'hidden' }}>
        {/* Linke Spalte: Sitemap-Tree + Aktionen */}
        <aside style={{
          width: 240, flexShrink: 0,
          background: 'var(--bg-app)', borderRight: '1px solid var(--border-light)',
          padding: 16, overflowY: 'auto',
        }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 8 }}>
            Sitemap
          </div>
          {sitemapPages.length === 0 ? (
            // Fallback: flache Liste aus wireframeData wenn keine sitemapPages
            <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 18px 0', display: 'flex', flexDirection: 'column', gap: 2 }}>
              {wfPages.map((p) => (
                <li key={p.page_id}>
                  <PageButton
                    label={p.page_name || `Seite ${p.page_id}`}
                    isActive={p.page_id === activePageId}
                    onClick={() => setActivePageId(p.page_id)}
                  />
                </li>
              ))}
            </ul>
          ) : (
            <div style={{ marginBottom: 18 }}>
              <SitemapTreeNodes
                nodes={sitemapTree.get('root') || []}
                tree={sitemapTree}
                activePageId={activePageId}
                onSelect={setActivePageId}
                wfPagesIds={new Set(wfPages.map((p) => p.page_id))}
                level={0}
              />
            </div>
          )}

          <div style={{
            fontSize: 12, fontWeight: 700, color: 'var(--text-secondary)',
            textTransform: 'uppercase', letterSpacing: '0.1em',
            marginBottom: 8, paddingTop: 16, borderTop: '1px solid var(--border-light)',
          }}>
            Aktionen
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <button
              type="button" onClick={uebernehmen}
              disabled={!renderedHTML || !activeSitemapPage || uebernahme.status === 'laeuft'}
              title={activeSitemapPage
                ? 'Schreibt diese Vorschau als Entwurf auf die Seite — von dort '
                  + 'geht sie in GrapesJS und in den Deploy.'
                : 'Diese Wireframe-Seite hat keine Entsprechung in der Sitemap.'}
              style={{
                ...btnPrimary,
                opacity: renderedHTML && activeSitemapPage ? 1 : 0.5,
                cursor: renderedHTML && activeSitemapPage ? 'pointer' : 'not-allowed',
              }}
            >
              {uebernahme.status === 'laeuft' ? 'Übernimmt…' : 'Auf die Seite übernehmen'}
            </button>
            {uebernahme.text && (
              <div style={{
                fontSize: 12, lineHeight: 1.4, padding: '6px 8px', borderRadius: 6,
                background: uebernahme.status === 'fehler' ? '#fef2f2' : '#f0fdf4',
                color: uebernahme.status === 'fehler' ? '#991b1b' : '#166534',
                border: `1px solid ${uebernahme.status === 'fehler' ? '#fca5a5' : '#86efac'}`,
              }}>{uebernahme.text}</div>
            )}
            <button type="button" onClick={() => onOpenGrapesJS?.(activePageId)} style={btnSecondary}>
              In GrapesJS öffnen
            </button>
            <button type="button" onClick={exportHTML} disabled={!renderedHTML}
              style={{ ...btnSecondary, opacity: renderedHTML ? 1 : 0.5, cursor: renderedHTML ? 'pointer' : 'not-allowed' }}>
              HTML exportieren
            </button>
            <button type="button" onClick={() => onNetlifyDeploy?.(projectId)} style={btnAccent}>
              Netlify Deploy
            </button>
          </div>
        </aside>

        {/* Rechte Spalte: Live-Vorschau */}
        <main style={{ flex: 1, overflow: 'auto', background: 'var(--surface)' }}>
          {/* Page-Header in der Vorschau (Breadcrumb aus Sitemap) */}
          {activeSitemapPage && (
            <div style={{
              padding: '10px 18px',
              background: '#fff', borderBottom: '1px solid var(--border-light)',
              fontSize: 12, color: 'var(--text-secondary)',
              display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap',
            }}>
              <Breadcrumb page={activeSitemapPage} sitemapPages={sitemapPages} />
              {activeSitemapPage.ist_pflichtseite && (
                <span title="Pflichtseite" style={{ marginLeft: 4 }}>🔒</span>
              )}
              <span style={{ flex: 1 }} />
              <span style={{ fontFamily: 'ui-monospace, monospace', fontSize: 12, color: 'var(--text-tertiary)' }}>
                /{slugify(activeSitemapPage.page_name)}
              </span>
            </div>
          )}

          <div style={{
            margin: '20px auto',
            background: styleGuide?.colors?.background || styleGuide?.palette?.bg_primary || '#fff',
            color: styleGuide?.colors?.text || '#0a0a0a',
            maxWidth: styleGuide?.spacing?.container || 1200,
            minHeight: '85%',
            boxShadow: '0 4px 16px rgba(0,0,0,0.08)',
            borderRadius: 8, overflow: 'hidden',
            fontFamily: styleGuide?.typography?.font_family || 'Noto Sans',
          }}>
            {renderedHTML ? (
              // eslint-disable-next-line react/no-danger
              <div dangerouslySetInnerHTML={{ __html: renderedHTML }} />
            ) : (
              <div style={{ padding: 60, textAlign: 'center', color: 'var(--text-secondary)' }}>
                {libraryLoading ? 'Vorschau wird gerendert…' : 'Diese Seite hat keine Blöcke.'}
              </div>
            )}
          </div>
        </main>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Source-Badge (Status-Bar oben)
// ─────────────────────────────────────────────────────────────────────────────

function SourceBadge({ label, value, icon }) {
  return (
    <div style={{
      display: 'inline-flex', alignItems: 'center', gap: 6,
      padding: '4px 10px',
      background: '#fff', border: '1px solid var(--border-light)', borderRadius: 6,
    }}>
      <span aria-hidden style={{ fontSize: 12 }}>{icon}</span>
      <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
        {label}
      </span>
      <span style={{ fontSize: 12, fontWeight: 700, color: KC_DARK }}>{value}</span>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Sitemap-Tree-Renderer (rekursiv)
// ─────────────────────────────────────────────────────────────────────────────

function SitemapTreeNodes({ nodes, tree, activePageId, onSelect, wfPagesIds, level }) {
  return (
    <ul style={{
      listStyle: 'none', padding: 0, margin: 0,
      display: 'flex', flexDirection: 'column', gap: 2,
    }}>
      {nodes.map((p) => {
        const children = tree.get(p.id) || [];
        const hasWireframe = wfPagesIds.has(p.id);
        return (
          <li key={p.id}>
            <PageButton
              label={p.page_name || `Seite ${p.id}`}
              isActive={p.id === activePageId}
              onClick={() => onSelect(p.id)}
              isPflicht={p.ist_pflichtseite}
              hasWireframe={hasWireframe}
              indent={level}
            />
            {children.length > 0 && (
              <SitemapTreeNodes
                nodes={children} tree={tree}
                activePageId={activePageId} onSelect={onSelect}
                wfPagesIds={wfPagesIds} level={level + 1}
              />
            )}
          </li>
        );
      })}
    </ul>
  );
}

function PageButton({ label, isActive, onClick, isPflicht, hasWireframe = true, indent = 0 }) {
  return (
    <button
      type="button" onClick={onClick}
      title={!hasWireframe ? `${label} — kein Wireframe vorhanden` : label}
      style={{
        width: '100%', textAlign: 'left',
        padding: '6px 10px', paddingLeft: 10 + indent * 14,
        borderRadius: 6, border: 'none',
        background: isActive ? KC_DARK : 'transparent',
        color: isActive ? '#fff' : 'var(--text-secondary)',
        fontSize: 12, fontWeight: isActive ? 700 : 500,
        cursor: 'pointer', fontFamily: 'inherit',
        display: 'flex', alignItems: 'center', gap: 6,
        opacity: hasWireframe ? 1 : 0.55,
      }}
    >
      <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {label}
      </span>
      {isPflicht && <span aria-label="Pflichtseite" style={{ fontSize: 12 }}>🔒</span>}
      {!hasWireframe && (
        <span title="Kein Wireframe" style={{ fontSize: 12, opacity: 0.7 }}>—</span>
      )}
    </button>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Breadcrumb in der Vorschau-Topbar
// ─────────────────────────────────────────────────────────────────────────────

function Breadcrumb({ page, sitemapPages }) {
  const path = [];
  let current = page;
  let safety = 10;
  const byId = new Map(sitemapPages.map((p) => [p.id, p]));
  while (current && safety-- > 0) {
    path.unshift(current);
    current = current.parent_id ? byId.get(current.parent_id) : null;
  }
  return (
    <span>
      {path.map((p, i) => (
        <span key={p.id}>
          {i > 0 && <span style={{ color: 'var(--text-45)', margin: '0 6px' }}>›</span>}
          <span style={{ color: i === path.length - 1 ? KC_DARK : 'var(--text-secondary)', fontWeight: i === path.length - 1 ? 700 : 500 }}>
            {p.page_name}
          </span>
        </span>
      ))}
    </span>
  );
}


// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────



function slugify(s) {
  if (!s) return '';
  return String(s).toLowerCase()
    .replace(/ä/g, 'ae').replace(/ö/g, 'oe').replace(/ü/g, 'ue').replace(/ß/g, 'ss')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

const btnBase = {
  width: '100%',
  border: 'none',
  borderRadius: 8,
  padding: '8px 12px',
  fontSize: 12,
  fontWeight: 700,
  cursor: 'pointer',
  textAlign: 'center',
  fontFamily: 'inherit',
};
const btnPrimary = { ...btnBase, background: KC_DARK, color: '#fff' };
const btnSecondary = { ...btnBase, background: 'transparent', color: KC_DARK, border: `1.5px solid ${KC_DARK}` };
const btnAccent = { ...btnBase, background: KC_YELLOW, color: '#000', textTransform: 'uppercase', letterSpacing: '0.04em' };
